import React from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { locizePlugin } from 'locize'; // does things for direct edit ??
import LocizeBackend from 'i18next-locize-backend';
import i18n from 'i18next';

import { Settings } from 'luxon';
import { useSelector } from 'react-redux';
import { selectAppConfig } from '../../slices/appState';

import { merge } from 'lodash';

const i18nConfig = {
  detection: {
    caches: ['cookie'],
    order: ['querystring', 'cookie', 'localStorage', 'navigator'],
    lookupQuerystring: 'culture',
  },
  compatibilityJSON: 'v3',
  interpolation: { prefix: '{{', suffix: '}}' },
  fallbackLng: 'de',
  ns: ['bookingjs'],
  load: 'languageOnly',
  backend: {
    projectId: 'ea068e6d-18b6-4697-bd21-65291883e27e',
    referenceLng: 'de',
  },
};

// Module-level init for backward compatibility (iframe paths get a fresh module per iframe)
const i18next = i18n
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  .use(locizePlugin)
  .use(LocizeBackend)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init(i18nConfig)
  .then(() => {
    Settings.defaultLocale = i18n.resolvedLanguage;
  })
  .then(async () => {
    i18n.fetchingLanguages =
      i18n.services.backendConnector.backend.getLanguages();

    i18n.loadedLanguages =
      await i18n.services.backendConnector.backend.getLanguages();
  });

/**
 * Creates a new isolated i18n instance for multi-instance usage.
 * Each call to init() should create its own i18n instance so that
 * language changes and resource bundles don't bleed between widgets.
 */
export function createI18nInstance() {
  const instance = i18n.createInstance();
  // locizePlugin is intentionally omitted here. It's only needed on the
  // module-level singleton for the "click to edit" dev feature. When a
  // child instance also registers it, the plugin's "save missing" tracker
  // injects empty-string markers for keys discovered via the parent page's
  // i18next instance (which shares the same locize project ID), polluting
  // the child's resource store with 25+ phantom empty keys. LocizeBackend
  // alone fetches the real translations cleanly.
  instance.readyPromise = instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .use(LocizeBackend)
    .init(i18nConfig)
    .then(() => {
      Settings.defaultLocale = instance.resolvedLanguage;
    })
    .then(async () => {
      instance.fetchingLanguages =
        instance.services.backendConnector.backend.getLanguages();

      instance.loadedLanguages =
        await instance.services.backendConnector.backend.getLanguages();
    });
  return instance;
}

export default function LanguageHandler() {
  const { i18n: i18nInstance } = useTranslation(undefined, {
    useSuspense: false,
  });
  const appConfig = useSelector(selectAppConfig);

  React.useEffect(() => {
    let active = true;
    setupLanguage();
    return () => {
      active = false;
    };

    async function setupLanguage() {
      if (appConfig) {
        // Wait for the i18n instance to be ready.
        // Per-instance i18n stores its promise on readyPromise;
        // the global i18n uses the module-level i18next promise.
        await (i18nInstance.readyPromise || i18next);

        if (appConfig.localization && active) {
          for (const [lang, bundle] of Object.entries(appConfig.localization)) {
            if (i18nInstance.hasResourceBundle(lang, 'bookingjs')) {
              const existingBundle = i18nInstance.getResourceBundle(
                lang,
                'bookingjs',
              );
              i18nInstance.removeResourceBundle(lang, 'bookingjs');
              const mergedBundle = merge({}, existingBundle, bundle);
              i18nInstance.addResourceBundle(lang, 'bookingjs', mergedBundle);
            } else {
              i18nInstance.addResourceBundle(lang, 'bookingjs', bundle);
            }
          }
        }

        if (
          appConfig.culture &&
          appConfig.culture !== i18nInstance.resolvedLanguage &&
          active
        ) {
          await i18nInstance.changeLanguage(appConfig.culture);
          Settings.defaultLocale = i18nInstance.resolvedLanguage;
        }
      }
    }
  }, [appConfig, i18nInstance]);
}
